/**
 * Das Menü der Verwaltung, einmal für Web und App (10.10./11.10.2026).
 *
 * Das Web zeichnet daraus Seitenleiste und Tab-Leiste (`frontend/src/components/Layout/
 * verwaltungsMenue.ts`), die App ihre Startseite der Verwaltung (`mobile/app/verwaltung/`).
 * `pfad` ist die Adresse im Web und zugleich der Schlüssel für Zähler und Symbole.
 * `rolle`: wer den Punkt sieht — `redaktion` = Admin und Moderator, `admin` = nur Admin,
 * `nurModerator` = nur Moderatoren (die Nutzerzahlen, die Admins in der Nutzerverwaltung sehen).
 */
export type VerwaltungsRolle = 'redaktion' | 'admin' | 'nurModerator';

export interface VerwaltungsPunkt {
  pfad: string;
  label: string;
  rolle: VerwaltungsRolle;
}

export interface VerwaltungsAbschnitt {
  label?: string;
  /** Beim ersten Besuch zugeklappt (nur Web-Seitenleiste). */
  zugeklappt?: boolean;
  punkte: VerwaltungsPunkt[];
}

export const VORSCHLAEGE_PFAD = '/moderation/vorschlaege';
export const DUPLIKATE_PFAD = '/moderation/duplikate';

export const VERWALTUNG_MENUE: VerwaltungsAbschnitt[] = [
  { punkte: [{ pfad: '/admin/start', label: 'Übersicht', rolle: 'redaktion' }] },
  {
    label: 'Prüfen',
    punkte: [
      { pfad: VORSCHLAEGE_PFAD, label: 'Vorschläge', rolle: 'redaktion' },
      { pfad: DUPLIKATE_PFAD, label: 'Duplikate', rolle: 'redaktion' },
      { pfad: '/organizers/zugaenge', label: 'Veranstalter-Zugänge', rolle: 'redaktion' },
      { pfad: '/admin/anfragen', label: 'Anfragen', rolle: 'redaktion' },
      { pfad: '/admin/feedback', label: 'Nutzer-Feedback', rolle: 'redaktion' },
      { pfad: '/admin/winterchallenge', label: 'Winterchallenge', rolle: 'redaktion' },
    ],
  },
  {
    label: 'Inhalte',
    punkte: [
      { pfad: '/events/manage', label: 'Events', rolle: 'redaktion' },
      { pfad: '/admin/anmeldestarts', label: 'Anmeldestarts', rolle: 'redaktion' },
      { pfad: '/admin/rennkalender', label: 'Profi-Rennkalender', rolle: 'redaktion' },
      { pfad: '/admin/news', label: 'News', rolle: 'redaktion' },
      { pfad: '/admin/rundmail', label: 'Rundmail', rolle: 'redaktion' },
    ],
  },
  {
    label: 'Veranstalter & Verkauf',
    punkte: [
      { pfad: '/organizers', label: 'Veranstalter', rolle: 'redaktion' },
      { pfad: '/admin/buchungen', label: 'Buchungsanfragen', rolle: 'redaktion' },
      { pfad: '/admin/preise', label: 'Preise', rolle: 'redaktion' },
      { pfad: '/admin/werbeplatz', label: 'Werbeplatz', rolle: 'redaktion' },
      { pfad: '/admin/outreach', label: 'Outreach', rolle: 'redaktion' },
    ],
  },
  {
    label: 'Auswertung',
    punkte: [
      { pfad: '/admin/auswertung', label: 'Suche & Besucher', rolle: 'redaktion' },
      { pfad: '/admin/statistiken', label: 'Klick-Statistiken', rolle: 'redaktion' },
      { pfad: '/admin/geschaeft', label: 'Geschäftszahlen', rolle: 'admin' },
      { pfad: '/events/most-favorited', label: 'Beliebteste Events', rolle: 'redaktion' },
      { pfad: '/admin/overview', label: 'Event-Übersicht', rolle: 'redaktion' },
      { pfad: '/users', label: 'Nutzerzahlen', rolle: 'nurModerator' },
    ],
  },
  {
    label: 'Daten & Agenten',
    zugeklappt: true,
    punkte: [
      { pfad: '/enrichment-queue', label: 'Enrichment-Queue', rolle: 'admin' },
      { pfad: '/agent-state', label: 'Agent-Status', rolle: 'admin' },
      { pfad: '/agent-runs', label: 'Agent-Läufe', rolle: 'admin' },
      { pfad: '/event-sources', label: 'Quellen', rolle: 'admin' },
      { pfad: '/audit-logs', label: 'Audit-Log', rolle: 'admin' },
    ],
  },
  {
    label: 'System & Hilfe',
    punkte: [
      { pfad: '/users', label: 'Nutzerverwaltung', rolle: 'admin' },
      { pfad: '/admin/email', label: 'E-Mails', rolle: 'admin' },
      { pfad: '/admin/neuerungen', label: 'Neuerungen', rolle: 'redaktion' },
      { pfad: '/admin/handbuch', label: 'Handbuch', rolle: 'redaktion' },
    ],
  },
];

/** Das Menü für eine Rolle: Punkte, die sie nicht sieht, und leere Abschnitte fallen weg. */
export function verwaltungsMenueFuer(rolle: string | undefined | null): VerwaltungsAbschnitt[] {
  const admin = rolle === 'ADMIN';
  const redaktion = admin || rolle === 'MODERATOR';
  if (!redaktion) return [];
  const sichtbar = (p: VerwaltungsPunkt) =>
    p.rolle === 'redaktion' || (p.rolle === 'admin' ? admin : !admin);
  return VERWALTUNG_MENUE
    .map(a => ({ ...a, punkte: a.punkte.filter(sichtbar) }))
    .filter(a => a.punkte.length > 0);
}
